<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import PostSaleAssignmentModal from './PostSaleAssignmentModal.vue';
import PostSaleCancellationModal from './PostSaleCancellationModal.vue';
import SalesFinalizeDialog from './SalesFinalizeDialog.vue';

const props = defineProps({
  agents: { type: Array, default: () => [] },
  assigning: { type: Boolean, default: false },
  cancelling: { type: Boolean, default: false },
  completing: { type: Boolean, default: false },
  caseData: { type: Object, required: true },
  loadingAgents: { type: Boolean, default: false },
  showStatus: { type: Boolean, default: true },
});
const emit = defineEmits([
  'assign',
  'cancel',
  'complete',
  'correction',
  'nucleo',
  'status',
]);
const { t } = useI18n();
const showAssignment = ref(false);
const showCancellation = ref(false);
const showCompletion = ref(false);

const canCancel = computed(
  () =>
    (props.caseData.permissions?.can_cancel ??
      props.caseData.permissions?.can_manage) &&
    !['finalizado', 'cancelado'].includes(props.caseData.status)
);
const canComplete = computed(
  () =>
    (props.caseData.permissions?.can_complete ??
      props.caseData.permissions?.can_manage) &&
    !['finalizado', 'cancelado'].includes(props.caseData.status)
);
const completionDisabled = computed(() => props.caseData.events?.length === 2);

const openAssignment = () => {
  showAssignment.value = true;
};

const confirmAssignment = responsibleUserId => {
  emit('assign', responsibleUserId);
  showAssignment.value = false;
};

const confirmCancellation = payload => {
  showCancellation.value = false;
  emit('cancel', payload);
};
const confirmCompletion = () => {
  showCompletion.value = false;
  emit('complete');
};
</script>

<template>
  <section
    class="flex flex-wrap justify-end gap-2 rounded-lais border border-lais-border-soft bg-white p-3"
    :aria-label="t('POST_SALE.DETAIL.ACTIONS_LABEL')"
  >
    <LaisPrimaryButton
      v-if="showStatus && caseData.permissions?.can_manage"
      icon="i-lucide-arrow-right-left"
      @click="emit('status')"
    >
      {{ t('POST_SALE.ACTIONS.CHANGE_STATUS') }}
    </LaisPrimaryButton>
    <LaisPrimaryButton
      v-if="canComplete"
      icon="i-lucide-check"
      :disabled="completionDisabled"
      @click="showCompletion = true"
    >
      {{ t('POST_SALE.ACTIONS.COMPLETE_PROCESS') }}
    </LaisPrimaryButton>
    <LaisPrimaryButton
      v-if="caseData.permissions?.can_request_correction"
      variant="ghost"
      icon="i-lucide-undo-2"
      :disabled="caseData.status === 'correcao'"
      @click="emit('correction')"
    >
      {{ t('POST_SALE.ACTIONS.RETURN_SALE') }}
    </LaisPrimaryButton>
    <LaisPrimaryButton
      v-if="caseData.permissions?.can_request_nucleo"
      variant="ghost"
      icon="i-lucide-file-plus-2"
      @click="emit('nucleo')"
    >
      {{ t('NUCLEO_WORK_ORDERS.REQUEST.ACTIONS.OPEN') }}
    </LaisPrimaryButton>
    <LaisPrimaryButton
      v-if="caseData.permissions?.can_assign"
      variant="ghost"
      icon="i-lucide-user-round-cog"
      @click="openAssignment"
    >
      {{ t('POST_SALE.ACTIONS.CHANGE_RESPONSIBLE') }}
    </LaisPrimaryButton>
    <LaisPrimaryButton
      v-if="canCancel"
      variant="ghost"
      class="!border-lais-danger !text-lais-danger hover:!border-lais-danger hover:!bg-red-50"
      icon="i-lucide-trash-2"
      @click="showCancellation = true"
    >
      {{ t('POST_SALE.ACTIONS.CANCEL_SALE') }}
    </LaisPrimaryButton>
  </section>

  <PostSaleAssignmentModal
    :agents="agents"
    :assigning="assigning"
    :loading-agents="loadingAgents"
    :show="showAssignment"
    @close="showAssignment = false"
    @confirm="confirmAssignment"
  />

  <PostSaleCancellationModal
    :case-data="caseData"
    :is-loading="cancelling"
    :show="showCancellation"
    @close="showCancellation = false"
    @confirm="confirmCancellation"
  />
  <SalesFinalizeDialog
    :is-loading="completing"
    :post-sale-status="caseData.status"
    :show="showCompletion"
    @close="showCompletion = false"
    @confirm="confirmCompletion"
  />
</template>
